import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote, ExternalLink } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { API } from "@/lib/faro";

function Stars({ n = 5, className = "" }) {
  return (
    <div className={`flex gap-0.5 ${className}`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={16} className={i < n ? "fill-[var(--faro-yellow)] text-[var(--faro-yellow)]" : "text-gray-300"} />
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [meta, setMeta] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    axios.get(`${API}/meta`).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  if (!reviews.length) {
    return (
      <section id="opiniones" className="section-pad bg-[var(--faro-bg)]" data-testid="reviews-section-empty">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <span className="eyebrow">Opiniones</span>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">
            Lo que dicen nuestros alumnos
          </h2>
          <p className="mt-4 text-lg text-[var(--faro-text-soft)]">
            Más de 45 años formando conductores en Vigo. Lee las opiniones reales de nuestros
            alumnos directamente en Google.
          </p>
          <a
            href={meta?.google_reviews_url || "https://www.google.com/search?q=autoescuelas+faro+vigo+opiniones"}
            target="_blank"
            rel="noreferrer"
            data-testid="reviews-google-link"
            className="btn-pill btn-primary mt-6 inline-flex"
          >
            Ver opiniones en Google <ExternalLink size={15} />
          </a>
        </div>
      </section>
    );
  }

  // Mobile: show 3 by default, else show all. Desktop always shows all (grid handles it)
  const isMobileView = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
  const visible = showAll || !isMobileView ? reviews : reviews.slice(0, 3);

  return (
    <section id="opiniones" className="section-pad bg-[var(--faro-bg)]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <Reveal className="max-w-2xl">
            <span className="eyebrow">Opiniones reales</span>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">
              Lo que dicen quienes ya aprobaron.
            </h2>
          </Reveal>

          {meta?.google_rating && meta?.google_rating_count && (
            <Reveal delay={0.1}>
              <a
                href={meta.google_reviews_url}
                target="_blank"
                rel="noreferrer"
                data-testid="google-rating-badge"
                className="inline-flex items-center gap-4 rounded-2xl border border-[rgba(30,42,143,0.12)] bg-[var(--faro-bg)] px-5 py-4 hover:border-[var(--faro-blue)] transition-colors"
              >
                <div className="font-display font-black text-4xl text-[var(--faro-blue)] tabular-nums">
                  {meta.google_rating}
                </div>
                <div>
                  <Stars n={Math.round(parseFloat(meta.google_rating))} />
                  <div className="text-xs text-[var(--faro-text-soft)] mt-1 inline-flex items-center gap-1">
                    {meta.google_rating_count}+ reseñas en Google <ExternalLink size={11} />
                  </div>
                </div>
              </a>
            </Reveal>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((r, i) => (
            <Reveal key={r.id} delay={(i % 3) * 0.08}>
              <figure
                data-testid={`review-card-${r.id}`}
                className="h-full rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white p-6 hover:-translate-y-1 hover:shadow-xl hover:shadow-[rgba(30,42,143,0.12)] transition-all"
              >
                <Quote size={28} className="text-[var(--faro-yellow)]" />
                <blockquote className="mt-3 text-[var(--faro-text)] leading-relaxed">“{r.text}”</blockquote>
                <figcaption className="mt-5 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-[var(--faro-text)]">{r.author}</div>
                    {r.permit && (
                      <div className="text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-red)]">Permiso {r.permit}</div>
                    )}
                  </div>
                  <Stars n={r.rating} />
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        {reviews.length > 3 && (
          <div className="mt-6 flex justify-center md:hidden">
            <button
              type="button"
              onClick={() => setShowAll((s) => !s)}
              data-testid="reviews-toggle"
              className="btn-pill btn-ghost text-sm"
            >
              {showAll ? "Ver menos" : `Ver todas las ${reviews.length} opiniones`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
